/**
 * Unified Debug tab: combines Re-Run Lab and Processing Journal in one view.
 *
 * Sub-sections toggled by buttons: "Re-Run Lab" (email parsing pipeline debug)
 * and "Journal" (read-only viewer for processing pipeline runs).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
    Play,
    Square,
    CheckCircle,
    XCircle,
    AlertTriangle,
    RefreshCw05,
    Mail01,
} from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Badge } from "@/components/base/badges/badges";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api-client";
import { storage } from "@/lib/local-storage";
import { useDebugStream, type DebugLogEntry } from "@/hooks/api/use-debug-stream";
import type { CompanyResponse } from "@/types/api/admin";
import { StepDetailPanel } from "@/components/admin/step-inspector";
import { UnifiedFilterBar } from "@/components/admin/unified-filter-bar";
import { cx } from "@/utils/cx";
import { ProcessingJournalTab } from "./processing-journal-tab";
import { AccountResetSection } from "./account-reset-section";

// ============================================================================
// Types
// ============================================================================

interface DebugUser {
    user_id: string;
    email: string;
    display_name: string;
    email_accounts: Array<{ account_id: string; email_address: string }>;
}

interface StartRunBody {
    company_id: string;
    user_id?: string;
    email_account_id?: string;
    date_from?: string;
    date_to?: string;
    exclude_email_ids?: string[];
    include_limbo?: boolean;
}

interface StartRunResponse {
    run_id: string;
    status: string;
    emails_total: number;
}

interface RunStatusResponse {
    run_id: string;
    company_id: string;
    user_id: string;
    mode: string;
    status: string;
    emails_total: number;
    emails_processed: number;
    error_count: number;
}

interface RunListResponse {
    runs: RunStatusResponse[];
    total: number;
}

/** Minimal email info extracted from the batch SSE stream or planned changes */
interface EmailListItem {
    emailId: string;
    sender: string;
    subject: string;
    stepsCompleted: number;
    stepsFailed: number;
    stepsTotal: number;
    status: "processing" | "done" | "error";
    emailStatus?: string; // original email status: pending, processed, irrelevant, etc.
}

interface PlannedChangesResponse {
    summary?: Record<string, number>;
    changes?: Array<{
        email_id: string;
        email_subject: string;
        items: Array<Record<string, unknown>>;
    }>;
}

interface EmailSearchItem {
    message_id: string;
    subject: string | null;
    from_address: string | null;
    received_at: string | null;
    status: string | null;
    has_attachments: boolean;
    has_steps: boolean;
    steps_completed: number;
    steps_total: number;
}

interface EmailSearchResponse {
    emails: EmailSearchItem[];
    total: number;
}

/** Sorted serialization so two URLSearchParams with the same keys compare equal */
function stableSerializeSearchParams(sp: URLSearchParams): string {
    return [...sp.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${v}`)
        .join("&");
}

interface PipelineDebugUrlState {
    activeSection: "rerun" | "journal" | "reset";
    companyId: string;
    userId: string;
    emailAccountId: string;
    dateFrom: string;
    dateTo: string;
    includeLimbo: boolean;
    emailSearchQuery: string;
    emailStatusFilter: string;
    journalRunStatus: string | null;
    journalTriggerType: string | null;
    selectedJournalRunId: string | null;
}

function buildPipelineDebugSearchParams(state: PipelineDebugUrlState): URLSearchParams {
    const p = new URLSearchParams();
    if (state.activeSection === "journal") p.set("view", "journal");
    if (state.activeSection === "reset") p.set("view", "reset");
    if (state.companyId) p.set("company", state.companyId);
    if (state.userId) p.set("user", state.userId);
    if (state.emailAccountId) p.set("email_account", state.emailAccountId);
    if (state.dateFrom) p.set("from", state.dateFrom);
    if (state.dateTo) p.set("to", state.dateTo);
    if (state.includeLimbo) p.set("limbo", "1");
    const q = state.emailSearchQuery.trim();
    if (q) p.set("q", q);
    if (state.emailStatusFilter) p.set("email_status", state.emailStatusFilter);
    if (state.journalRunStatus) p.set("j_status", state.journalRunStatus);
    if (state.journalTriggerType) p.set("j_trigger", state.journalTriggerType);
    if (state.selectedJournalRunId) p.set("run", state.selectedJournalRunId);
    return p;
}

// ============================================================================
// Helpers
// ============================================================================

function truncate(text: string, max: number): string {
    return text.length > max ? text.slice(0, max) + "..." : text;
}

function emailStatusIcon(item: EmailListItem) {
    if (item.status === "processing") {
        return <RefreshCw05 className="h-4 w-4 text-brand-secondary animate-spin shrink-0" />;
    }
    if (item.stepsFailed > 0) {
        return <XCircle className="h-4 w-4 text-error-primary shrink-0" />;
    }
    if (item.stepsCompleted >= item.stepsTotal && item.stepsTotal > 0) {
        return <CheckCircle className="h-4 w-4 text-success-primary shrink-0" />;
    }
    return <Mail01 className="h-4 w-4 text-tertiary shrink-0" />;
}

const EMAIL_STATUS_BADGE: Record<string, { label: string; className: string }> = {
    pending: { label: "Pending", className: "bg-warning-secondary text-warning-primary" },
    processed: { label: "Processed", className: "bg-success-secondary text-success-primary" },
    irrelevant: { label: "Irrelevant", className: "bg-secondary text-tertiary" },
    limbo: { label: "Limbo", className: "bg-warning-secondary text-warning-primary" },
    training: { label: "Training", className: "bg-brand-secondary text-brand-primary" },
    user_approved: { label: "Approved", className: "bg-success-secondary text-success-primary" },
    user_rejected: { label: "Rejected", className: "bg-error-secondary text-error-primary" },
};

// ============================================================================
// Sub-components
// ============================================================================

function ProgressBar({ processed, total, currentSubject }: { processed: number; total: number; currentSubject: string }) {
    const pct = total > 0 ? Math.round((processed / total) * 100) : 0;

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
                <span className="text-secondary truncate max-w-[70%]">
                    {currentSubject ? `Processing: "${currentSubject}"` : "Processing..."}
                </span>
                <span className="text-tertiary shrink-0">{processed} / {total} emails ({pct}%)</span>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div
                    className="h-full rounded-full bg-brand-solid transition-all duration-300"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function EmailListPanel({
    emails,
    selectedId,
    onSelect,
    isRunning,
}: {
    emails: EmailListItem[];
    selectedId: string | null;
    onSelect: (emailId: string) => void;
    isRunning: boolean;
}) {
    const listRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when new emails are added during a run
    useEffect(() => {
        if (isRunning && listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [emails.length, isRunning]);

    if (emails.length === 0 && !isRunning) {
        return (
            <div className="flex-1 flex items-center justify-center p-6">
                <p className="text-tertiary text-sm text-center">
                    No emails yet. Run a batch or reload a previous run.
                </p>
            </div>
        );
    }

    return (
        <div ref={listRef} className="flex-1 overflow-y-auto min-h-0">
            {emails.map((item) => (
                <button
                    key={item.emailId}
                    type="button"
                    className={`w-full text-left px-4 py-3 border-b border-secondary transition-colors ${selectedId === item.emailId
                        ? "bg-brand-primary_alt"
                        : "hover:bg-secondary/50"
                        }`}
                    onClick={() => onSelect(item.emailId)}
                >
                    <div className="flex items-start gap-2">
                        {emailStatusIcon(item)}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-primary truncate">
                                {item.sender || "Unknown sender"}
                            </p>
                            <p className="text-xs text-tertiary truncate mt-0.5">
                                {truncate(item.subject || "No subject", 60)}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1">
                                {item.emailStatus && EMAIL_STATUS_BADGE[item.emailStatus] && (
                                    <span className={cx(
                                        "px-1.5 py-0.5 text-[9px] font-semibold rounded-full uppercase",
                                        EMAIL_STATUS_BADGE[item.emailStatus].className
                                    )}>
                                        {EMAIL_STATUS_BADGE[item.emailStatus].label}
                                    </span>
                                )}
                                <span className="text-[10px] text-tertiary font-mono">
                                    {item.stepsCompleted}/{item.stepsTotal} steps
                                    {item.stepsFailed > 0 && (
                                        <span className="text-error-primary ml-1">
                                            ({item.stepsFailed} failed)
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </button>
            ))}
        </div>
    );
}

// ============================================================================
// Main tab component
// ============================================================================

export function PipelineDebugTab() {
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const lastWrittenUrl = useRef<string>("");
    const didHydrateFromUrl = useRef(false);

    // Filter state
    const [companyId, setCompanyId] = useState<string>("");
    const [userId, setUserId] = useState<string>("");
    const [emailAccountId, setEmailAccountId] = useState<string>("");
    const [dateFrom, setDateFrom] = useState<string>("");
    const [dateTo, setDateTo] = useState<string>("");
    const [includeLimbo, setIncludeLimbo] = useState<boolean>(false);
    const [emailStatusFilter, setEmailStatusFilter] = useState<string>("");

    // Run state — restore from sessionStorage on mount
    const [runId, setRunId] = useState<string | null>(() => {
        try { return sessionStorage.getItem("debug_runId"); } catch { return null; }
    });
    const [startError, setStartError] = useState<string | null>(null);
    const [isStarting, setIsStarting] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [applyResult, setApplyResult] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processProgress, setProcessProgress] = useState<{ index: number; total: number; subject: string } | null>(null);
    const processAbortRef = useRef<AbortController | null>(null);

    // Email list state — restore from sessionStorage on mount
    const [emailList, setEmailList] = useState<EmailListItem[]>(() => {
        try {
            const saved = sessionStorage.getItem("debug_emailList");
            return saved ? JSON.parse(saved) : [];
        } catch { return []; }
    });
    const [selectedEmailId, setSelectedEmailId] = useState<string | null>(() => {
        try { return sessionStorage.getItem("debug_selectedEmailId"); } catch { return null; }
    });
    const [manualEmailId, setManualEmailId] = useState("");

    // Email search state
    const [emailSearchQuery, setEmailSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(emailSearchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [emailSearchQuery]);

    // Persist to sessionStorage on change
    useEffect(() => {
        try {
            if (runId) sessionStorage.setItem("debug_runId", runId);
            else sessionStorage.removeItem("debug_runId");
        } catch { /* ignore */ }
    }, [runId]);

    useEffect(() => {
        try {
            if (emailList.length > 0) sessionStorage.setItem("debug_emailList", JSON.stringify(emailList));
            else sessionStorage.removeItem("debug_emailList");
        } catch { /* ignore */ }
    }, [emailList]);

    useEffect(() => {
        try {
            if (selectedEmailId) sessionStorage.setItem("debug_selectedEmailId", selectedEmailId);
            else sessionStorage.removeItem("debug_selectedEmailId");
        } catch { /* ignore */ }
    }, [selectedEmailId]);

    // Track which emails we've already seen in the SSE stream
    const seenEmailsRef = useRef<Set<string>>(new Set());
    const currentEmailRef = useRef<{ emailId: string; subject: string; sender: string } | null>(null);

    const { logs, progress, summary, isRunning, connect, disconnect } = useDebugStream();

    // Sub-section toggle
    const [activeSection, setActiveSection] = useState<"rerun" | "journal" | "reset">("rerun");

    const [journalRunStatus, setJournalRunStatus] = useState<string | null>(null);
    const [journalTriggerType, setJournalTriggerType] = useState<string | null>(null);
    const [selectedJournalRunId, setSelectedJournalRunId] = useState<string | null>(null);

    const applyParamsFromSearchParams = useCallback((sp: URLSearchParams) => {
        const view = sp.get("view");
        setActiveSection(view === "journal" ? "journal" : view === "reset" ? "reset" : "rerun");
        setCompanyId(sp.get("company") ?? "");
        setUserId(sp.get("user") ?? "");
        setEmailAccountId(sp.get("email_account") ?? "");
        setDateFrom(sp.get("from") ?? "");
        setDateTo(sp.get("to") ?? "");
        setIncludeLimbo(sp.get("limbo") === "1");
        const q = sp.get("q") ?? "";
        setEmailSearchQuery(q);
        setDebouncedSearchQuery(q.trim());
        setEmailStatusFilter(sp.get("email_status") ?? "");
        const js = sp.get("j_status");
        setJournalRunStatus(js && js.length > 0 ? js : null);
        const jt = sp.get("j_trigger");
        setJournalTriggerType(jt && jt.length > 0 ? jt : null);
        const run = sp.get("run");
        setSelectedJournalRunId(run && run.length > 0 ? run : null);
    }, []);

    // Hydrate filter state once from the current URL (refresh / deep link)
    useLayoutEffect(() => {
        const sp = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
        applyParamsFromSearchParams(sp);
        lastWrittenUrl.current = stableSerializeSearchParams(sp);
        didHydrateFromUrl.current = true;
    }, [applyParamsFromSearchParams]);

    // Browser back/forward: apply when the router search string changes externally
    useEffect(() => {
        if (!didHydrateFromUrl.current) return;
        const s = stableSerializeSearchParams(new URLSearchParams(searchParams));
        if (s === lastWrittenUrl.current) return;
        lastWrittenUrl.current = s;
        applyParamsFromSearchParams(new URLSearchParams(searchParams));
    }, [searchParams, applyParamsFromSearchParams]);

    useEffect(() => {
        if (!didHydrateFromUrl.current) return;
        const next = buildPipelineDebugSearchParams({
            activeSection,
            companyId,
            userId,
            emailAccountId,
            dateFrom,
            dateTo,
            includeLimbo,
            emailSearchQuery,
            emailStatusFilter,
            journalRunStatus,
            journalTriggerType,
            selectedJournalRunId,
        });
        const nextStr = stableSerializeSearchParams(next);
        if (nextStr === lastWrittenUrl.current) return;
        lastWrittenUrl.current = nextStr;
        setSearchParams(next, { replace: true });
    }, [
        activeSection,
        companyId,
        userId,
        emailAccountId,
        dateFrom,
        dateTo,
        includeLimbo,
        emailSearchQuery,
        emailStatusFilter,
        journalRunStatus,
        journalTriggerType,
        selectedJournalRunId,
        setSearchParams,
    ]);

    // Companies list
    const { data: companies = [] } = useQuery({
        queryKey: ["admin", "companies"],
        queryFn: () => apiGet<CompanyResponse[]>("/companies", { params: { limit: 500 } }),
    });

    // Drop company if it is not in the loaded list (e.g. stale URL)
    const companyInitRef = useRef(false);
    useEffect(() => {
        if (companies.length === 0) return;
        if (companyId && !companies.some((c) => c.company_id === companyId)) {
            setCompanyId("");
            setUserId("");
            setEmailAccountId("");
            companyInitRef.current = false;
        }
    }, [companies, companyId]);

    // Auto-select company on first load only when URL / hydrate left it empty
    useEffect(() => {
        if (companyInitRef.current || companies.length === 0) return;
        if (companyId) {
            companyInitRef.current = true;
            return;
        }
        companyInitRef.current = true;
        const saved = storage.getItem("debug_last_company_id");
        if (saved && companies.some((c) => c.company_id === saved)) {
            setCompanyId(saved);
        } else if (companies.length === 1) {
            setCompanyId(companies[0].company_id);
        }
    }, [companies, companyId]);

    // Persist company selection
    useEffect(() => {
        if (companyId) storage.setItem("debug_last_company_id", companyId);
    }, [companyId]);

    // Debug users list (only when company is selected)
    const { data: debugUsers = [] } = useQuery({
        queryKey: ["admin", "debug", "users", companyId],
        queryFn: () =>
            apiGet<DebugUser[]>("/admin/debug/users", {
                params: companyId ? { company_id: companyId } : undefined,
            }),
        enabled: !!companyId,
    });

    // Derived email accounts from selected user
    const emailAccounts = userId
        ? (debugUsers.find((u) => u.user_id === userId)?.email_accounts ?? [])
        : debugUsers.flatMap((u) => u.email_accounts);

    // Email search — triggered by Search button
    // Auto-search: triggers whenever company, search query, or date filters change
    const searchQueryParams = useMemo(() => {
        if (!companyId) return null;
        const params: Record<string, string> = { company_id: companyId, limit: "50" };
        if (debouncedSearchQuery.length >= 2) params.subject = debouncedSearchQuery;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (emailStatusFilter) params.status = emailStatusFilter;
        return params;
    }, [companyId, debouncedSearchQuery, dateFrom, dateTo, emailStatusFilter]);

    const { data: searchResults } = useQuery({
        queryKey: ["admin", "debug", "email-search", searchQueryParams],
        queryFn: () =>
            apiGet<EmailSearchResponse>("/admin/debug/emails/search", {
                params: searchQueryParams!,
            }),
        enabled: !!searchQueryParams,
    });

    // Convert search results to EmailListItem format for display
    const searchEmailList: EmailListItem[] = useMemo(() => {
        if (!searchResults?.emails) return [];
        return searchResults.emails.map((e) => ({
            emailId: e.message_id,
            sender: e.from_address || "",
            subject: e.subject || "",
            stepsCompleted: e.steps_completed,
            stepsFailed: 0,
            stepsTotal: e.steps_total,
            status: "done" as const,
            emailStatus: e.status || undefined,
        }));
    }, [searchResults]);

    // =========================================================================
    // Persistency: load last run on mount when company is selected
    // =========================================================================
    const { data: lastRunsData } = useQuery({
        queryKey: ["admin", "debug", "runs", companyId],
        queryFn: () =>
            apiGet<RunListResponse>("/admin/debug/runs", {
                params: { company_id: companyId, limit: "1" },
            }),
        enabled: !!companyId,
    });

    // Load emails from the last run's planned_changes
    const lastRunId = lastRunsData?.runs?.[0]?.run_id ?? null;
    const lastRunStatus = lastRunsData?.runs?.[0]?.status ?? null;

    // Auto-reconnect to active run on mount (only if we don't already have data)
    useEffect(() => {
        if (lastRunStatus === "running" && lastRunId && !isRunning) {
            setRunId(lastRunId);
            // Only reconnect to SSE if we don't have cached email data
            if (emailList.length === 0) {
                const token = storage.getItem("access_token");
                if (token) {
                    connect(lastRunId, token);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastRunStatus, lastRunId]);

    const { data: lastRunPlan } = useQuery({
        queryKey: ["admin", "debug", "run-plan", lastRunId],
        queryFn: () =>
            apiGet<PlannedChangesResponse>(`/admin/debug/reprocess/${lastRunId}/plan`),
        enabled: !!lastRunId && (lastRunStatus === "completed" || lastRunStatus === "cancelled") && emailList.length === 0,
    });

    // Populate email list from last run's planned changes on mount
    useEffect(() => {
        if (lastRunPlan?.changes && emailList.length === 0 && !runId) {
            const items: EmailListItem[] = lastRunPlan.changes.map((c) => ({
                emailId: c.email_id,
                sender: "",
                subject: c.email_subject || "",
                stepsCompleted: 0,
                stepsFailed: 0,
                stepsTotal: 22,
                status: "done" as const,
            }));
            if (items.length > 0) {
                setEmailList(items);
                setRunId(String(lastRunId));
            }
        }
    }, [lastRunPlan, emailList.length, runId, lastRunId]);

    // =========================================================================
    // Build email list from batch SSE stream logs
    // =========================================================================
    useEffect(() => {
        // Process new log entries to track emails
        for (const entry of logs) {
            if (entry.step_type === "email_context") {
                // Extract email info from the email_context step
                const raw = entry as DebugLogEntry & Record<string, unknown>;
                const emailId = String(raw.email_message_id || "");
                const subject = entry.step_name?.replace(/^Email:\s*/, "") || entry.decision_summary || "";
                const sender = String(raw.sender || raw.from_address || "");

                if (emailId && !seenEmailsRef.current.has(emailId)) {
                    seenEmailsRef.current.add(emailId);
                    const prevId = currentEmailRef.current?.emailId;
                    currentEmailRef.current = { emailId, subject, sender };

                    // Single setState: mark previous as done + add new one
                    setEmailList((prev) => {
                        const updated = prevId
                            ? prev.map((e) =>
                                e.emailId === prevId ? { ...e, status: "done" as const } : e
                            )
                            : prev;
                        return [
                            ...updated,
                            {
                                emailId,
                                sender,
                                subject,
                                stepsCompleted: 0,
                                stepsFailed: 0,
                                stepsTotal: 22,
                                status: "processing" as const,
                            },
                        ];
                    });
                }
            }
        }

        // Update step counts for current emails based on progress events
        if (progress && currentEmailRef.current) {
            setEmailList((prev) => {
                const idx = prev.findIndex((e) => e.emailId === currentEmailRef.current?.emailId);
                if (idx === -1) return prev;

                // Count steps for this email from logs
                const emailLogs = logs.filter((l) => {
                    const raw = l as DebugLogEntry & Record<string, unknown>;
                    return raw.email_message_id === currentEmailRef.current?.emailId ||
                        l.step_type === "email_context";
                });

                const completed = emailLogs.filter((l) => l.status === "success").length;
                const failed = emailLogs.filter((l) => l.status === "failed").length;

                const updated = [...prev];
                updated[idx] = {
                    ...updated[idx],
                    stepsCompleted: completed,
                    stepsFailed: failed,
                };
                return updated;
            });
        }
    }, [logs, progress]);

    // Mark all emails as done when the batch run finishes
    useEffect(() => {
        if (summary && !isRunning) {
            setEmailList((prev) =>
                prev.map((e) => ({
                    ...e,
                    status: "done" as const,
                }))
            );
        }
    }, [summary, isRunning]);

    // =========================================================================
    // Handlers
    // =========================================================================

    const handleCompanyChange = useCallback((id: string) => {
        setCompanyId(id);
        setUserId("");
        setEmailAccountId("");
    }, []);

    const handleUserChange = useCallback((id: string) => {
        setUserId(id);
        setEmailAccountId("");
    }, []);

    const handleRunDry = useCallback(async () => {
        if (!companyId) {
            setStartError("Select a company before running.");
            return;
        }
        if (!userId) {
            setStartError("Select a user before running.");
            return;
        }

        setStartError(null);
        setApplyResult(null);
        setIsStarting(true);

        // Collect already-processed email IDs for resume (don't clear the list)
        const processedIds = emailList
            .filter((e) => e.status === "done")
            .map((e) => e.emailId);
        const isResume = processedIds.length > 0;

        if (!isResume) {
            // Fresh run — clear everything
            setEmailList([]);
            setSelectedEmailId(null);
            seenEmailsRef.current.clear();
            currentEmailRef.current = null;
        }
        setRunId(null);

        try {
            const body: StartRunBody = { company_id: companyId };
            if (userId) body.user_id = userId;
            if (emailAccountId) body.email_account_id = emailAccountId;
            if (dateFrom) body.date_from = dateFrom;
            if (dateTo) body.date_to = dateTo;
            if (includeLimbo) body.include_limbo = includeLimbo;
            if (isResume) body.exclude_email_ids = processedIds;

            const result = await apiPost<StartRunResponse>("/admin/debug/reprocess", body);
            setRunId(result.run_id);

            // Get token from storage and start SSE stream
            const token = storage.getItem("access_token");
            if (!token) {
                setStartError("No auth token found. Please log in again.");
                return;
            }

            connect(result.run_id, token);
        } catch (err: unknown) {
            let message = "Failed to start run";
            if (err instanceof Error) {
                // Try to extract a human-readable message from API validation errors
                try {
                    const parsed = JSON.parse(err.message);
                    if (Array.isArray(parsed)) {
                        message = parsed.map((e: Record<string, unknown>) => `${e.loc ? (e.loc as string[]).slice(-1)[0] : "field"}: ${e.msg}`).join(", ");
                    } else {
                        message = err.message;
                    }
                } catch {
                    message = err.message;
                }
            }

            // If a run is already active, reconnect to it instead of showing an error
            const activeMatch = message.match(/Active debug run already exists:\s*([\w-]+)/);
            if (activeMatch) {
                const activeRunId = activeMatch[1];
                setRunId(activeRunId);
                const token = storage.getItem("access_token");
                if (token) {
                    connect(activeRunId, token);
                    setStartError(null);
                } else {
                    setStartError("Found active run but no auth token. Please log in again.");
                }
            } else {
                setStartError(message);
            }
        } finally {
            setIsStarting(false);
        }
    }, [companyId, userId, emailAccountId, dateFrom, dateTo, includeLimbo, emailList, connect]);

    const handleCancel = useCallback(async () => {
        if (!runId) return;
        setIsCancelling(true);
        try {
            disconnect();
            await apiPost(`/admin/debug/reprocess/${runId}/cancel`);
            // Mark current processing email as done, keep list for resume
            setEmailList((prev) =>
                prev.map((e) => e.status === "processing" ? { ...e, status: "done" as const } : e)
            );
            setRunId(null);
            // Don't clear emailList or selectedEmailId — needed for resume
        } catch {
            // Ignore cancel errors
        } finally {
            setIsCancelling(false);
        }
    }, [runId, disconnect]);

    const handleApply = useCallback(async () => {
        if (!runId) return;
        setIsApplying(true);
        setApplyResult(null);
        try {
            await apiPost<{ run_id: string }>(`/admin/debug/reprocess/${runId}/apply`);
            setApplyResult("Changes applied successfully.");
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Apply failed";
            setApplyResult(`Error: ${message}`);
        } finally {
            setIsApplying(false);
        }
    }, [runId]);

    const handleDiscard = useCallback(() => {
        setRunId(null);
        setApplyResult("Run discarded.");
    }, []);

    const handleNewRun = useCallback(() => {
        setRunId(null);
        setEmailList([]);
        setSelectedEmailId(null);
        setApplyResult(null);
        setStartError(null);
        setEmailSearchQuery("");
        seenEmailsRef.current.clear();
        currentEmailRef.current = null;
    }, []);

    const handleProcessPending = useCallback(async () => {
        if (!companyId) return;
        processAbortRef.current?.abort();
        const controller = new AbortController();
        processAbortRef.current = controller;
        setIsProcessing(true);
        setProcessProgress(null);
        setApplyResult(null);

        const token = storage.getItem("access_token");
        if (!token) return;

        const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
        const prefix = import.meta.env.VITE_API_VERSION_PREFIX || "/api/v1";
        const url = `${baseUrl.replace(/\/$/, "")}${prefix.startsWith("/") ? prefix : `/${prefix}`}/admin/debug/process-pending`;

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ company_id: companyId, limit: 500 }),
                signal: controller.signal,
            });

            if (!response.ok) {
                const text = await response.text().catch(() => response.statusText);
                setApplyResult(`Error: ${text}`);
                setIsProcessing(false);
                return;
            }

            const reader = response.body?.getReader();
            if (!reader) { setIsProcessing(false); return; }

            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() || "";
                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    try {
                        const evt = JSON.parse(line.slice(6));
                        if (evt.type === "processing") {
                            setProcessProgress({ index: evt.index, total: evt.total, subject: evt.subject || "" });
                        } else if (evt.type === "complete") {
                            setApplyResult(`Done: ${evt.processed} processed, ${evt.failed} failed out of ${evt.total}`);
                        }
                    } catch { /* skip */ }
                }
            }
        } catch (err: unknown) {
            setApplyResult(`Error: ${err instanceof Error ? err.message : "Connection failed"}`);
        } finally {
            setIsProcessing(false);
            setProcessProgress(null);
        }
    }, [companyId]);

    const handleSelectEmail = useCallback((emailId: string) => {
        setSelectedEmailId(emailId);
    }, []);

    const canRun = !!companyId && !isRunning && !isStarting;

    // Build select items
    const companyItems = companies.map((c) => ({ id: c.company_id, label: c.company_name }));
    const userItems = debugUsers.map((u) => ({ id: u.user_id, label: u.display_name || u.email }));
    const emailAccountItems = emailAccounts.map((a) => ({ id: a.account_id, label: a.email_address }));

    return (
        <div className="space-y-4 flex flex-col h-[calc(100vh-2rem)]">
            {/* Header with sub-section toggle */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-primary">Debug</h2>
                    <p className="text-tertiary text-sm mt-1">
                        Inspect email pipeline steps, re-run processing, and browse platform journal.
                    </p>
                </div>
                {/* Sub-section toggle buttons */}
                <div className="flex gap-1 rounded-lg border border-secondary p-0.5 bg-secondary/30">
                    <button
                        type="button"
                        onClick={() => setActiveSection("rerun")}
                        className={cx(
                            "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                            activeSection === "rerun"
                                ? "bg-primary text-primary shadow-sm"
                                : "text-tertiary hover:text-secondary"
                        )}
                    >
                        Email Pipeline
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveSection("journal")}
                        className={cx(
                            "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                            activeSection === "journal"
                                ? "bg-primary text-primary shadow-sm"
                                : "text-tertiary hover:text-secondary"
                        )}
                    >
                        Journal
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveSection("reset")}
                        className={cx(
                            "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                            activeSection === "reset"
                                ? "bg-primary text-primary shadow-sm"
                                : "text-tertiary hover:text-secondary"
                        )}
                    >
                        Account Reset
                    </button>
                </div>
            </div>

            {/* Active section content */}
            {activeSection === "rerun" ? (
            <>
            {/* Email pipeline filter bar */}
            <UnifiedFilterBar
                mode="debug"
                search={emailSearchQuery}
                onSearchChange={(v) => setEmailSearchQuery(v)}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={(v) => setDateFrom(v)}
                onDateToChange={(v) => setDateTo(v)}
                companyId={companyId}
                companies={companyItems}
                onCompanyChange={handleCompanyChange}
                userId={userId}
                users={userItems}
                onUserChange={handleUserChange}
                emailAccountId={emailAccountId}
                emailAccounts={emailAccountItems}
                onEmailAccountChange={(id) => setEmailAccountId(id)}
                includeLimbo={includeLimbo}
                onIncludeLimboChange={setIncludeLimbo}
                isDisabled={isRunning}
            />
            {/* Batch action bar */}
            <div className="flex items-center gap-3 flex-wrap">
                {/* Left: action buttons */}
                <div className="flex items-center gap-2">
                    {!isRunning && !isProcessing && (
                        <>
                            <Button
                                color="primary"
                                size="sm"
                                onPress={() => { handleNewRun(); setTimeout(handleRunDry, 100); }}
                                isDisabled={!canRun}
                                isLoading={isStarting}
                            >
                                Batch Dry-Run
                            </Button>
                            <Button
                                color="primary-destructive"
                                size="sm"
                                onPress={handleProcessPending}
                                isDisabled={!companyId}
                                isLoading={isProcessing}
                            >
                                Process Pending (LIVE)
                            </Button>
                        </>
                    )}
                    {isProcessing && (
                        <>
                            <div className="flex items-center gap-3">
                                <RefreshCw05 className="size-4 text-brand-secondary animate-spin" />
                                <span className="text-sm text-secondary">
                                    {processProgress
                                        ? `Processing ${processProgress.index}/${processProgress.total}: ${processProgress.subject.slice(0, 40)}...`
                                        : "Starting..."}
                                </span>
                            </div>
                            <Button
                                color="secondary-destructive"
                                size="sm"
                                onPress={() => processAbortRef.current?.abort()}
                            >
                                <span className="inline-flex items-center gap-1.5"><Square className="size-3.5 shrink-0" /> Stop</span>
                            </Button>
                        </>
                    )}
                    {!isRunning && runId && emailList.length > 0 && (
                        <>
                            <Button
                                color="primary"
                                size="sm"
                                onPress={handleRunDry}
                                isDisabled={!canRun}
                                isLoading={isStarting}
                            >
                                <Play className="size-4" />
                                Resume
                            </Button>
                            <Button color="secondary" size="sm" onPress={handleNewRun}>
                                New Run
                            </Button>
                        </>
                    )}
                    {isRunning && (
                        <Button
                            color="secondary"
                            size="sm"
                            onPress={handleCancel}
                            isLoading={isCancelling}
                        >
                            <Square className="size-4" />
                            Cancel
                        </Button>
                    )}
                </div>

                {/* Center: progress bar (during batch runs) */}
                {(isRunning || (emailList.length > 0 && runId)) && (
                    <div className="flex-1 min-w-[200px]">
                        <ProgressBar
                            processed={progress?.emails_processed ?? emailList.filter(e => e.status === "done").length}
                            total={progress?.emails_total ?? emailList.length}
                            currentSubject={currentEmailRef.current?.subject ?? ""}
                        />
                    </div>
                )}

                {/* Right: apply/discard after completion */}
                {!isRunning && runId && summary && (
                    <div className="flex items-center gap-2 ml-auto">
                        <Button color="primary" size="sm" onPress={handleApply} isLoading={isApplying}>
                            Run Live (re-process)
                        </Button>
                        <Button color="secondary" size="sm" onPress={handleDiscard}>
                            Discard Preview
                        </Button>
                    </div>
                )}

                {/* Status messages */}
                {startError && (
                    <div className="flex items-center gap-1.5 text-sm text-error-primary">
                        <AlertTriangle className="size-4 shrink-0" />
                        {startError}
                    </div>
                )}
                {applyResult && (
                    <div className={`text-sm ${applyResult.startsWith("Error") ? "text-error-primary" : "text-success-primary"}`}>
                        {applyResult}
                    </div>
                )}
            </div>

            {/* Email List + Step Inspector — simple two-panel layout */}
            <div className="rounded-lg border border-secondary bg-primary overflow-hidden flex-1 min-h-0">
                <div className="flex h-full">
                    {/* Left panel: email list (batch run OR search results) */}
                    <div className="w-80 shrink-0 border-r border-secondary flex flex-col min-h-0">
                        <div className="px-4 py-3 border-b border-secondary shrink-0 space-y-2">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-primary">
                                    {emailList.length > 0 && runId ? "Batch Run" : "Emails"}
                                    <span className="ml-2 text-tertiary font-normal">
                                        ({emailList.length > 0 && runId ? emailList.length : searchResults?.total ?? 0})
                                    </span>
                                </h3>
                            </div>
                            {/* Status filter chips */}
                            <div className="flex gap-1 flex-wrap">
                                {[
                                    { value: "", label: "All" },
                                    { value: "pending", label: "Pending" },
                                    { value: "processed", label: "Processed" },
                                    { value: "training", label: "Training" },
                                    { value: "limbo", label: "Limbo" },
                                    { value: "irrelevant", label: "Irrelevant" },
                                ].map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setEmailStatusFilter(opt.value)}
                                        className={cx(
                                            "px-2 py-0.5 text-[11px] font-medium rounded-full transition-colors",
                                            emailStatusFilter === opt.value
                                                ? "bg-brand-solid text-white"
                                                : "bg-secondary text-tertiary hover:text-primary"
                                        )}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {emailList.length > 0 && runId ? (
                            /* Batch run email list */
                            <EmailListPanel
                                emails={emailList}
                                selectedId={selectedEmailId}
                                onSelect={handleSelectEmail}
                                isRunning={isRunning}
                            />
                        ) : (
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {!companyId ? (
                                <div className="flex items-center justify-center p-6">
                                    <p className="text-tertiary text-sm text-center">
                                        Select a company in the filters above to search emails.
                                    </p>
                                </div>
                            ) : searchEmailList.length === 0 ? (
                                <div className="flex items-center justify-center p-6">
                                    <p className="text-tertiary text-sm text-center">
                                        {debouncedSearchQuery
                                            ? <>No emails found for &ldquo;{debouncedSearchQuery}&rdquo;</>
                                            : "Type a subject to search, or leave empty to see recent emails."
                                        }
                                    </p>
                                </div>
                            ) : (
                                searchEmailList.map((item) => (
                                    <button
                                        key={item.emailId}
                                        type="button"
                                        className={`w-full text-left px-4 py-3 border-b border-secondary transition-colors ${selectedEmailId === item.emailId
                                            ? "bg-brand-primary_alt"
                                            : "hover:bg-secondary/50"
                                            }`}
                                        onClick={() => handleSelectEmail(item.emailId)}
                                    >
                                        <div className="flex items-start gap-2">
                                            {emailStatusIcon(item)}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <p className="text-sm font-medium text-primary truncate">
                                                        {item.sender || "Unknown sender"}
                                                    </p>
                                                    {item.stepsCompleted === 0 && (
                                                        <Badge color="gray" size="sm">No steps</Badge>
                                                    )}
                                                </div>
                                                <p className="text-xs text-tertiary truncate mt-0.5">
                                                    {truncate(item.subject || "No subject", 60)}
                                                </p>
                                                <div className="flex items-center gap-1.5 mt-1">
                                                    <span className="text-[10px] text-tertiary font-mono">
                                                        {item.stepsCompleted}/{item.stepsTotal} steps
                                                    </span>
                                                    {item.emailStatus && (
                                                        <Badge
                                                            color={
                                                                item.emailStatus === "pending" ? "warning"
                                                                : item.emailStatus === "processed" ? "success"
                                                                : item.emailStatus === "limbo" ? "brand"
                                                                : "gray"
                                                            }
                                                            size="sm"
                                                        >
                                                            {item.emailStatus}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                        )}
                    </div>

                    {/* Right panel: step inspector */}
                    <div className="flex-1 flex flex-col min-h-0 min-w-0 p-4 overflow-y-auto">
                        {selectedEmailId ? (
                            <StepDetailPanel
                                emailId={selectedEmailId}
                                companyId={companyId}
                                onStatusChange={() => {
                                    queryClient.invalidateQueries({ queryKey: ["admin", "debug", "email-search"] });
                                }}
                            />
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center gap-4">
                                <p className="text-tertiary text-sm text-center">
                                    Search for an email on the left, then select it to inspect and re-run pipeline steps.
                                </p>
                                <div className="flex items-center gap-2 w-full max-w-lg">
                                    <Input
                                        label=""
                                        placeholder="Or paste email message UUID here..."
                                        value={manualEmailId}
                                        onChange={(v) => setManualEmailId(v)}
                                        className="flex-1"
                                    />
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onPress={() => {
                                            if (manualEmailId.trim()) {
                                                setSelectedEmailId(manualEmailId.trim());
                                            }
                                        }}
                                        isDisabled={!manualEmailId.trim()}
                                    >
                                        Inspect
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            </>
            ) : activeSection === "journal" ? (
                <>
                {/* Journal filter bar — date range + status/trigger type filters */}
                <UnifiedFilterBar
                    mode="journal"
                    search={emailSearchQuery}
                    onSearchChange={(v) => setEmailSearchQuery(v)}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateFromChange={(v) => setDateFrom(v)}
                    onDateToChange={(v) => setDateTo(v)}
                    status={journalRunStatus}
                    onStatusChange={setJournalRunStatus}
                    triggerType={journalTriggerType}
                    onTriggerTypeChange={setJournalTriggerType}
                />
                <ProcessingJournalTab
                    searchText={debouncedSearchQuery}
                    dateFrom={dateFrom || undefined}
                    dateTo={dateTo || undefined}
                    runStatus={journalRunStatus}
                    triggerType={journalTriggerType}
                    onRunStatusChange={setJournalRunStatus}
                    onTriggerTypeChange={setJournalTriggerType}
                    selectedRunId={selectedJournalRunId}
                    onSelectedRunIdChange={setSelectedJournalRunId}
                    onClearSearch={() => {
                        setEmailSearchQuery("");
                        setDateFrom("");
                        setDateTo("");
                        setSelectedJournalRunId(null);
                        setJournalRunStatus(null);
                        setJournalTriggerType(null);
                    }}
                />
                </>
            ) : (
                <AccountResetSection />
            )}
        </div>
    );
}
